const {KitModule, registerModule} = window;

// Calm cadence, quick swaps. Keep SWAP_MS in step with the longest
// --logoCloud-swap-* delay + duration in logoCloud.scss.
const WAVE_GAP_MS = 1600;
const SLOTS_PER_WAVE = 3;
const STAGGER_MS = 160;
const SWAP_MS = 800;
const DECODE_TIMEOUT_MS = 4000;

/**
 * Resolve after `ms` milliseconds.
 * @param {number} ms
 * @returns {Promise<void>}
 */
function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Resolve after the current styles have been painted, so a transition
 * starts from them rather than from the element's final state.
 * @returns {Promise<void>}
 */
function nextPaint() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
}

/**
 * Shuffle an array in place (Fisher–Yates).
 * @template T
 * @param {T[]} items
 * @returns {T[]}
 */
function shuffle(items) {
    for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
}

/**
 * Integrations logo strip — 16 fixed slots; logos beyond them rotate in with a
 * staggered blur/opacity crossfade. Idles offscreen, in background tabs and
 * under reduced motion (the server-rendered first 16 simply stay put).
 */
class LogoCloud extends KitModule {
    onInitialize() {
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        // Logos waiting for a slot, in random order. Swapped-out logos rejoin
        // at the back, and picks come from the front half (see takeFromQueue).
        this.queue = shuffle(this.$pool ? [...this.$pool.content.children] : []);
        this.lastSlots = new Set();
        this.isVisible = false;
        this.tick = this.tick.bind(this);

        document.addEventListener('visibilitychange', this);
        this.reduceMotion.addEventListener('change', this);
    }

    onViewportEnter() {
        this.isVisible = true;
        this.start();
    }

    onViewportExit() {
        this.isVisible = false;
        this.stop();
    }

    onvisibilitychange() {
        if (document.hidden) {
            this.stop();
        } else {
            this.start();
        }
    }

    onchange() {
        if (this.reduceMotion.matches) {
            this.stop();
        } else {
            this.start();
        }
    }

    canAnimate() {
        return (
            this.isVisible &&
            !document.hidden &&
            !this.reduceMotion.matches &&
            this.queue.length > 0 &&
            (this.$slots || []).length > 0
        );
    }

    start() {
        if (this.timer || this.inFlight || !this.canAnimate()) return;
        this.timer = setTimeout(this.tick, WAVE_GAP_MS);
    }

    stop() {
        clearTimeout(this.timer);
        this.timer = null;
    }

    async tick() {
        this.timer = null;
        this.inFlight = true;
        await this.wave();
        this.inFlight = false;
        this.start();
    }

    /**
     * Swap a few random slots (never the ones from the previous wave).
     * Incoming logos are loaded first so the stagger isn't eaten by the network.
     */
    async wave() {
        const count = Math.min(SLOTS_PER_WAVE, this.queue.length);
        const $slots = this.pickSlots(count);
        const swaps = $slots.map(($slot) => ({
            $slot,
            $outgoing: $slot.querySelector('.frame'),
            $incoming: this.takeFromQueue(),
        }));

        const ready = await Promise.all(swaps.map((swap) => this.prepare(swap)));
        const staged = swaps.filter((_, i) => ready[i]);
        if (!staged.length) return;

        await nextPaint();
        staged.forEach((swap, i) => setTimeout(() => this.crossfade(swap), i * STAGGER_MS));
        await wait((staged.length - 1) * STAGGER_MS + SWAP_MS);

        staged.forEach(({$outgoing}) => {
            if (!$outgoing) return;
            $outgoing.remove();
            $outgoing.classList.remove('is-leaving');
            this.queue.push($outgoing);
        });
    }

    /**
     * Take a random logo from the front (longest-waiting) half of the queue,
     * so picks feel random but a logo that just left doesn't bounce back.
     * @returns {HTMLElement}
     */
    takeFromQueue() {
        const index = Math.floor(Math.random() * Math.ceil(this.queue.length / 2));
        return this.queue.splice(index, 1)[0];
    }

    /**
     * Pick `count` random slots, skipping those swapped in the previous wave.
     * @param {number} count
     * @returns {HTMLElement[]}
     */
    pickSlots(count) {
        const candidates = this.$slots.filter(($slot) => !this.lastSlots.has($slot));
        const picked = shuffle(candidates).slice(0, count);
        this.lastSlots = new Set(picked);
        return picked;
    }

    /**
     * Mount the incoming logo invisibly in its slot and wait for it to decode.
     * A slow logo goes back in the queue for a later wave; a broken one is
     * dropped from the rotation.
     * @param {{$slot: HTMLElement, $incoming: HTMLElement}} swap
     * @returns {Promise<boolean>}
     */
    async prepare({$slot, $incoming}) {
        const $img = $incoming.querySelector('img');
        if ($img) $img.loading = 'eager';
        $incoming.classList.add('is-entering');
        $slot.append($incoming);

        const timedOut = wait(DECODE_TIMEOUT_MS).then(() => 'timeout');
        const result = await Promise.race([
            ($img ? $img.decode() : Promise.resolve()).then(() => 'ok', () => 'error'),
            timedOut,
        ]);
        if (result === 'ok') return true;

        $incoming.remove();
        $incoming.classList.remove('is-entering');
        if (result === 'timeout') this.queue.push($incoming);
        return false;
    }

    /**
     * Blur the old logo out and the new one in (timings live in the SCSS).
     * @param {{$outgoing: HTMLElement|null, $incoming: HTMLElement}} swap
     */
    crossfade({$outgoing, $incoming}) {
        $outgoing?.classList.add('is-leaving');
        $incoming.classList.remove('is-entering');
    }
}

registerModule('logoCloud', LogoCloud);
